import type { GroupConsoleDevice } from '../../shared/wire';
import { MOCK } from '../state/device';
import { team } from '../state/team';
import { teamGatedIn, clearTeam, startConsolePoll } from '../map/team-layer';
import {
  apiGetGroupConsole,
  apiGroupClaim,
  apiGroupClaimRevoke,
  apiMintInvite,
} from '../api/team';
import { COMMAND_CATALOG, formatCommandResult } from '../api/command';
import { showToast } from './toast';
import { escapeHtml } from './dom';
import { relTime } from './format';

// Callbacks wired from main.ts to avoid a circular import
// (group-claim → main is not allowed; main wires these at module-eval time).
let _refreshAuthUiCb: (() => void) | null = null;
export function setRefreshAuthUiCb(fn: () => void): void { _refreshAuthUiCb = fn; }
let _copyTextCb: ((text: string) => void) | null = null;
export function setCopyTextCb(fn: (text: string) => void): void { _copyTextCb = fn; }

/* ── Group claim / invite / console actions (ADR 0025) ────────────────────── */
// Canonical URL form of the current device's group (the :g1-:g2 path segment).
export function currentGroupPath(): string | null {
  const g = MOCK.device?.group;
  return (g && g.length === 2) ? `${encodeURIComponent(g[0])}-${encodeURIComponent(g[1])}` : null;
}

// Group-claim state for the device-drawer group-share UI. The client had no
// group-claim-ownership signal before; now it reuses the live console `role`
// when the overlay poll has seeded for THIS group, else a one-shot probe.
export type GroupClaimState = 'checking' | 'unclaimed' | 'owner' | 'participant';
let groupClaimProbe: { path: string; state: 'unclaimed' | 'owner' | 'participant' } | null = null;
export function getGroupClaimProbe() { return groupClaimProbe; }
export function setGroupClaimProbe(v: typeof groupClaimProbe) { groupClaimProbe = v; }
export function currentGroupClaimState(gp: string): GroupClaimState {
  // Gated-in ⇒ console returned 200 ⇒ the group is claimed; team.role says which.
  if(teamGatedIn() && team.role) return team.role;
  if(getGroupClaimProbe()?.path === gp) return getGroupClaimProbe()!.state;
  return 'checking';
}
// One-shot resolve on panel open when the live poll hasn't seeded. 200 ⇒ claimed
// (body.role distinguishes owner/participant); 404 ⇒ unclaimed (sound: the active
// device's owner is by definition a participant of its own group, so a 404 means
// no active group claim, not "not a participant"). Re-renders on resolve.
export async function probeGroupClaim(gp: string){
  try {
    const data = await apiGetGroupConsole(gp);
    setGroupClaimProbe({ path: gp, state: data.role });
  } catch (err: any) {
    if(err?.status === 404) setGroupClaimProbe({ path: gp, state: 'unclaimed' });
    else return;                              // transient — leave 'checking', try again next open
  }
  _refreshAuthUiCb?.();
}
// The group-sharing button set for a given claim state. Element ids are kept
// (group-claim-btn / -revoke-btn / -invite-btn / -console-btn) so the existing
// bindings attach; absent ids no-op.
export function groupShareBody(state: GroupClaimState): string {
  if(state === 'checking') return `<div class="acct-hint">Checking group claim…</div>`;
  if(state === 'unclaimed')
    return `<div class="acct-hint">Claim this <b>group</b> to share command responses + timelines with the team. Only you can send commands to your own device; participants can view.</div>` +
      `<button class="menu-action" id="group-claim-btn"><span>Claim this group</span><i class="ph ph-users-three"></i></button>`;
  if(state === 'owner')
    return `<div class="acct-hint">You own this <b>group</b> claim — shared C2 is active. Mint an invite link to let teammates add devices; release to re-open the group.</div>` +
      `<button class="menu-action" id="group-invite-btn"><span>Mint invite link…</span><i class="ph ph-link"></i></button>` +
      `<button class="menu-action" id="group-console-btn"><span>Open group console</span><i class="ph ph-broadcast"></i></button>` +
      `<button class="menu-action" id="group-revoke-btn"><span>Release group claim</span><i class="ph ph-x-circle"></i></button>`;
  // participant: claimed by a teammate — view only.
  return `<div class="acct-hint">This <b>group</b> is claimed by a teammate. You can view the shared team picture; only the group owner manages invites and geofences.</div>` +
    `<button class="menu-action" id="group-console-btn"><span>Open group console</span><i class="ph ph-broadcast"></i></button>`;
}
export async function claimCurrentGroup(){
  const gp = currentGroupPath();
  if(!gp) return;
  try {
    await apiGroupClaim(gp);
    setGroupClaimProbe({ path: gp, state: 'owner' });   // we are now the owner; flip the UI immediately
    showToast('group claimed — shared C2 active');
    _refreshAuthUiCb?.();
    // The console poll may have self-stopped on a pre-claim 404 (group was
    // unclaimed when the map opened). Restart it so the team overlay (markers /
    // geofences / draw tools) seeds NOW — idempotent, fires pollConsole() at once.
    startConsolePoll();
  } catch (err: any) {
    if(err?.status === 403) showToast('claim a device in this group first');
    else if(err?.status === 409) showToast('group already claimed by another user');
    else if(err?.status === 401) showToast('please sign in again');
    else showToast('could not claim group');
  }
}
export async function revokeCurrentGroupClaim(){
  const gp = currentGroupPath();
  if(!gp) return;
  // No window.confirm() — it is suppressed (returns false, no dialog) in some
  // browsers/PWAs, which silently swallowed the release with zero feedback.
  // Release is reversible by re-claiming, so an immediate revoke + toast is fine.
  try {
    await apiGroupClaimRevoke(gp);
    setGroupClaimProbe({ path: gp, state: 'unclaimed' }); // re-opened; flip the UI immediately
    clearTeam();                                        // drop the overlay layer (poll would 404 next tick anyway)
    showToast('group claim released');
    _refreshAuthUiCb?.();
  } catch (err: any) {
    if(err?.status === 404) showToast('you do not own this group claim');
    else showToast('could not release claim');
  }
}
export async function mintInviteForCurrentGroup(){
  const gp = currentGroupPath();
  if(!gp) return;
  try {
    const inv = await apiMintInvite(gp);            // default: 7d, unlimited
    // The link carries BOTH the group key (g1-g2, the server resolves the group
    // from the POST body, never from the token) and the opaque invite token, so
    // routeJoinGroupHash can recover both. `gp` is already the encoded g1-g2.
    const link = `${location.origin}/#/join-group/${gp}/${encodeURIComponent(inv.invite_token)}`;
    _copyTextCb?.(link);
    showToast('invite link copied');
  } catch (err: any) {
    if(err?.status === 404) showToast('claim this group first (owner only)');
    else showToast('could not mint invite');
  }
}
export async function openGroupConsole(){
  const gp = currentGroupPath();
  if(!gp) return;
  try {
    const con = await apiGetGroupConsole(gp);
    showGroupConsoleModal(con.group, con.devices);
  } catch (err: any) {
    if(err?.status === 404) showToast('no shared group console (claim the group first)');
    else showToast('could not open group console');
  }
}

// Read-only participant console (ADR 0025): the team's opted-in devices, each
// with its latest position time + recent command/response history. No send
// control — only the device's own owner can enqueue.
export function showGroupConsoleModal(group: string[], devices: GroupConsoleDevice[]){
  document.getElementById('gc-modal')?.remove();
  const now = Date.now();
  const body = devices.length === 0
    ? `<div class="acct-hint">No group-shared devices reporting yet.</div>`
    : devices.map(dev => {
        const name = escapeHtml(dev.tag || dev.token.split('-').slice(-1)[0]);
        const last = dev.pings.length ? `last seen ${relTime(dev.pings[dev.pings.length - 1].t)}` : 'no position yet';
        const cmds = dev.commands.slice().reverse().slice(0, 6).map(c => {
          const label = escapeHtml(COMMAND_CATALOG.find(x => x.kind === c.kind)?.label ?? c.kind);
          let st: string;
          if(c.acked_at != null) st = `<span class="cmd-ok">${escapeHtml(formatCommandResult(c.kind, c.result_data))}</span>`;
          else if(c.expires_at < now) st = `<span class="cmd-dead">no response</span>`;
          else st = `<span class="cmd-wait">waiting…</span>`;
          return `<div class="cmd-row"><span class="cmd-kind">${label}</span>${st}<span class="cmd-when">${relTime(c.acked_at ?? c.issued_at)}</span></div>`;
        }).join('') || `<div class="acct-hint">No commands.</div>`;
        return `<div class="gc-dev"><div class="gc-dev-head"><b>${name}</b> <span class="gc-dev-sub">${last}</span></div>${cmds}</div>`;
      }).join('');
  const overlay = document.createElement('div');
  overlay.id = 'gc-modal';
  overlay.className = 'gc-modal-overlay';
  overlay.innerHTML =
    `<div class="gc-modal"><div class="gc-modal-head"><span>Group console · ${escapeHtml(group.join('-'))}</span>` +
    `<button class="gc-modal-close" id="gc-modal-close" aria-label="Close">✕</button></div>` +
    `<div class="acct-hint gc-modal-note">Read-only team view — only a device's own owner can send commands to it.</div>` +
    `<div class="gc-modal-body">${body}</div></div>`;
  overlay.addEventListener('click', (e) => { if(e.target === overlay) overlay.remove(); });
  overlay.querySelector('#gc-modal-close')?.addEventListener('click', () => overlay.remove());
  document.body.appendChild(overlay);
}
